import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";

type PoppedConv = {
  id: string;
  partnerName: string;
  partnerAvatar: string | null;
  minimized: boolean;
};

type Ctx = {
  popped: PoppedConv[];
  pop: (c: Omit<PoppedConv, "minimized">) => void;
  close: (id: string) => void;
  toggleMinimized: (id: string) => void;
  isPopped: (id: string) => boolean;
};

const PoppedConversationsContext = createContext<Ctx | null>(null);
const STORAGE_KEY = "popped-conversations:v1";

export const PoppedConversationsProvider = ({ children }: { children: ReactNode }) => {
  const [popped, setPopped] = useState<PoppedConv[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(popped));
    } catch {
      /* ignore quota */
    }
  }, [popped]);

  const pop = useCallback((c: Omit<PoppedConv, "minimized">) => {
    setPopped((prev) => {
      if (prev.some((p) => p.id === c.id)) {
        // bring forward by re-inserting at end & ensure unminimized
        return [...prev.filter((p) => p.id !== c.id), { ...c, minimized: false }].slice(-3);
      }
      return [...prev, { ...c, minimized: false }].slice(-3);
    });
  }, []);

  const close = useCallback((id: string) => {
    setPopped((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const toggleMinimized = useCallback((id: string) => {
    setPopped((prev) => prev.map((p) => (p.id === id ? { ...p, minimized: !p.minimized } : p)));
  }, []);

  const isPopped = useCallback((id: string) => popped.some((p) => p.id === id), [popped]);

  const value = useMemo(
    () => ({ popped, pop, close, toggleMinimized, isPopped }),
    [popped, pop, close, toggleMinimized, isPopped],
  );

  return <PoppedConversationsContext.Provider value={value}>{children}</PoppedConversationsContext.Provider>;
};

export const usePoppedConversations = () => {
  const ctx = useContext(PoppedConversationsContext);
  if (!ctx) throw new Error("usePoppedConversations must be used within PoppedConversationsProvider");
  return ctx;
};
